import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Trash2, RefreshCcw,
  CheckCircle, Tag, Clock, XCircle, FileSearch, FileText,
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { partitionStatusFromColumns } from '../../shared/utils/erpListColumnOrder';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import { useTranslation } from 'react-i18next';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { makeModulePageTranslators } from '../../shared/utils/pageI18n';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import QawaemTableEmptyRow from '../../shared/components/table/QawaemTableEmptyRow';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { erpFormLabelClass, ERP_MODAL_FORM_CLASS, ErpFieldLabel } from '../../shared/utils/erpFormFieldClasses';
import { resolveErpModalFormSections } from '../../shared/utils/erpModalFormSections';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useSettings } from '../../core/contexts/SettingsContext';
import {
  REQUEST_STATUS, WORKFLOW_STATUS, normalizeStatusValue, entityLabel,
  JSON_FORM_FIELDS, mapRecordJsonFields,
  APPROBATIONS_WORKFLOWS_REFRESH_EVENT, dispatchApprobationsWorkflowsRefresh,
  validateApprobationsRejectComment, parseChainSteps, stringifyChainSteps,
} from './approbationsShared';
import { useApprobationsConfig } from './useApprobationsConfig';
import { sliceApprobationsListItems } from './approbationsConfigDefaults';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
import ApprobationsChainStepsField from './ApprobationsChainStepsField';
import {
  useApprobationsEmployeeOptions,
  buildApprobationsEmployeeSelectOptions,
  enrichChainStepsWithEmployees,
  resolveApprobationsEmployeeDisplayName,
} from './approbationsListPageUtils';

function exportApprobationsCsv(rows, columns, filename) {
  if (!rows.length) return;
  const keys = columns.map((c) => c.key);
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    keys.join(','),
    ...rows.map((row) => keys.map((k) => escape(row[k])).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Shared CRUD shell for Approbations module pages.
 * @param {object} props
 * @param {string} props.ns - i18n namespace suffix (modules.approbations.pages.{ns})
 * @param {import('react').ComponentType} props.icon - Page header icon
 * @param {string} props.apiPath - API base path e.g. /api/approbations/workflows
 * @param {Array} props.columns - Table column definitions
 * @param {Function} props.computeStats - (records) => stat card config array (max 4 items)
 * @param {object} props.emptyForm - Default form state
 * @param {Array} props.formSections - Modal sections with fields
 * @param {Function} [props.mapRecordToForm] - Map DB row to form
 * @param {Function} [props.mapFormToPayload] - Map form to API payload
 * @param {Function} [props.filterRecords] - Optional extra filter
 * @param {string} [props.codeField] - Entity reference field name (e.g. request_code)
 * @param {string} [props.entityPrefixKey] - settings.configuration.entityPrefixes key
 * @param {string} [props.codePrefixFallback] - Fallback prefix when settings key is absent
 * @param {boolean} [props.codeEditable] - Allow editing رمز in modal (default true)
 * @param {boolean} [props.allowCreate] - Show create button (default true)
 * @param {string} [props.entityLabel] - Toast entity label for CRUD notifications
 * @param {string} [props.entityLabelPlural] - Toast entity label (plural) for bulk delete
 * @param {Function} [props.validateFormExtra] - Optional (formData, p, showNotify) => true when invalid (blocks save)
 * @param {Function} [props.onModalOpen] - Called when create/edit modal opens (refresh linked pickers)
 * @param {Function} [props.onFormFieldChange] - Optional (key, value, prevFormData) => nextFormData
 * @param {Function} [props.resolveFieldOptions] - Optional (field, formData) => select options override
 */
export default function ApprobationsCrudShell({
  ns,
  icon: PageIcon,
  apiPath,
  columns,
  computeStats,
  emptyForm,
  formSections,
  mapRecordToForm,
  mapFormToPayload,
  filterRecords,
  codeField = 'code',
  entityPrefixKey = '',
  codePrefixFallback = 'REF',
  codeEditable = true,
  allowCreate = true,
  gradient = 'from-indigo-500 to-indigo-700',
  statusField = 'status',
  statusOptions: statusOptionsProp = null,
  entityLabel: entityLabelProp = null,
  entityLabelPlural: entityLabelPluralProp = null,
  validateFormExtra = null,
  onModalOpen = null,
  onFormFieldChange = null,
  resolveFieldOptions = null,
}) {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const approbationsConfig = useApprobationsConfig();
  const { p, pc, pl } = makeModulePageTranslators(t, 'approbations', ns);
  const { employeeOptions, refresh: refreshEmployeeOptions } = useApprobationsEmployeeOptions({ valueField: 'id' });
  const employeeSelectOptions = useMemo(
    () => buildApprobationsEmployeeSelectOptions(employeeOptions, { emptyLabel: pc('select') }),
    [employeeOptions, pc],
  );
  const entityLabelText = entityLabelProp || pc('entity_label', { defaultValue: 'العنصر' });
  const entityLabelPluralText = entityLabelPluralProp || pc('entity_label_plural', { defaultValue: 'عناصر' });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode: isLight } = useGlobalTheme();
  const cfgTheme = useModuleConfigTheme();

  const i18nNsReady = i18n.isInitialized
    && i18n.exists(`modules.approbations.pages.${ns}.title`)
    && i18n.exists(`modules.approbations.pages.${ns}.${columns[0]?.labelKey || 'title'}`);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const {
    filters: columnFilters,
    setFilter: setColumnFilter,
    editingKey: columnEditingKey,
    setEditingKey: setColumnEditingKey,
  } = useColumnHeaderFilters();
  const modalFormLayout = useMemo(
    () => resolveErpModalFormSections(formSections),
    [formSections],
  );
  const resolvedFormSections = modalFormLayout.sections;
  const showFormTabs = modalFormLayout.showTabs;
  const defaultFormTab = resolvedFormSections[0]?.id || 'basic';
  const [formTab, setFormTab] = useState(defaultFormTab);
  const formTabs = useMemo(
    () => (showFormTabs
      ? resolvedFormSections.map((section) => ({
        id: section.id,
        label: p(section.titleKey),
        icon: section.icon || FileText,
      }))
      : []),
    [showFormTabs, resolvedFormSections, p],
  );
  const [formData, setFormData] = useState({ ...emptyForm });
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(columns.map((c) => [c.key, true]))
  );

  const defaultToForm = useCallback(
    (r) => mapRecordJsonFields(r, emptyForm),
    [emptyForm],
  );
  const toForm = mapRecordToForm || defaultToForm;
  const toPayload = mapFormToPayload || ((f) => ({ ...f }));

  const loadRecords = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get(apiPath);
      const data = response?.data ?? response;
      setRecords(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error(`Error loading ${apiPath}:`, error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRecords(); }, [refreshTrigger]);

  useEffect(() => {
    if (!apiPath.includes('/workflows')) return undefined;
    const handler = () => setRefreshTrigger((n) => n + 1);
    window.addEventListener(APPROBATIONS_WORKFLOWS_REFRESH_EVENT, handler);
    return () => window.removeEventListener(APPROBATIONS_WORKFLOWS_REFRESH_EVENT, handler);
  }, [apiPath]);

  useEffect(() => {
    if (!showModal && !showViewModal) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showModal, showViewModal]);

  useEffect(() => {
    if (viewMode === 'table' && searchTerm) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateCode = useCallback(() => {
    if (!codeField) return '';
    const prefix = getEntityPrefix(settings, entityPrefixKey, codePrefixFallback);
    const list = Array.isArray(records) ? records : [];
    return generateEntityReference(prefix, list, (r) => r[codeField]);
  }, [settings, entityPrefixKey, codePrefixFallback, records, codeField]);

  const resetForm = (keepModalOpen = false) => {
    setFormData({ ...emptyForm });
    setEditingRecord(null);
    setFormTab(defaultFormTab);
    if (!keepModalOpen) setShowModal(false);
  };

  const openCreateModal = () => {
    const initial = { ...emptyForm };
    if (codeField && codeEditable) {
      initial[codeField] = generateCode();
    }
    setFormData(initial);
    setEditingRecord(null);
    setFormTab(defaultFormTab);
    setShowModal(true);
    void refreshEmployeeOptions();
    onModalOpen?.();
  };

  const isFieldRequired = useCallback((field) => {
    if (field.required) return true;
    const label = p(field.labelKey);
    return /\*\s*$/.test(String(label).trim());
  }, [p]);

  const validateFormFields = useCallback(() => {
    for (const section of resolvedFormSections) {
      for (const field of section.fields || []) {
        if (field.readOnly || (codeField && field.key === codeField)) continue;
        if (!isFieldRequired(field)) continue;
        if (field.type === 'chain_steps') {
          const jsonKey = field.jsonKey || field.key;
          const steps = parseChainSteps(formData[jsonKey]);
          if (!steps.some((s) => s.employee_id != null && String(s.employee_id).trim() !== '')) {
            const label = String(p(field.labelKey)).replace(/\s*\*\s*$/, '').trim();
            notifyError(showNotify, pc('validation_required', { field: label, defaultValue: `الحقل مطلوب: ${label}` }));
            return false;
          }
          continue;
        }
        const val = formData[field.key];
        if (val == null || String(val).trim() === '') {
          const label = String(p(field.labelKey)).replace(/\s*\*\s*$/, '').trim();
          notifyError(showNotify, pc('validation_required', { field: label, defaultValue: `الحقل مطلوب: ${label}` }));
          return false;
        }
      }
    }
    const chainStepsKeys = new Set(
      resolvedFormSections.flatMap((s) => (s.fields || [])
        .filter((f) => f.type === 'chain_steps')
        .map((f) => f.jsonKey || f.key)),
    );
    for (const key of JSON_FORM_FIELDS) {
      if (chainStepsKeys.has(key)) continue;
      const val = formData[key];
      if (val == null || !String(val).trim()) continue;
      try {
        JSON.parse(String(val));
      } catch {
        notifyError(showNotify, pc('validation_json', { defaultValue: 'صيغة JSON غير صالحة' }));
        return false;
      }
    }
    return true;
  }, [resolvedFormSections, formData, codeField, isFieldRequired, p, pc, showNotify]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rejectMessage = pc('validation_reject_comment', { defaultValue: 'تعليق إلزامي عند الرفض' });
    if (validateFormExtra?.(formData, p, showNotify)) return;
    if (validateApprobationsRejectComment(approbationsConfig, formData, showNotify, notifyError, rejectMessage)) return;
    if (!validateFormFields()) return;
    try {
      setLoading(true);
      const enrichedForm = { ...formData };
      resolvedFormSections.forEach((section) => {
        (section.fields || []).forEach((field) => {
          if (field.type !== 'chain_steps') return;
          const jsonKey = field.jsonKey || field.key;
          enrichedForm[jsonKey] = stringifyChainSteps(
            enrichChainStepsWithEmployees(parseChainSteps(formData[jsonKey]), employeeOptions),
          );
        });
      });
      let payload = toPayload(enrichedForm, editingRecord);
      if (codeField && codeEditable) {
        const codeValue = (formData[codeField] || '').trim() || generateCode();
        payload = { ...payload, [codeField]: codeValue };
      }
      if (editingRecord?.id) {
        await localApiAdapter.put(`${apiPath}/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabelText);
      } else {
        await localApiAdapter.post(apiPath, payload);
        notifyCreated(showNotify, entityLabelText);
      }
      resetForm();
      if (apiPath.includes('/workflows')) {
        dispatchApprobationsWorkflowsRefresh();
      }
      setRefreshTrigger((n) => n + 1);
    } catch (error) {
      notifyError(showNotify, error?.response?.data?.error || pc('save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData(toForm(record));
    setFormTab(defaultFormTab);
    setShowModal(true);
    void refreshEmployeeOptions();
    onModalOpen?.();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(pc('delete_confirm'))) return;
    try {
      await localApiAdapter.delete(`${apiPath}/${id}`);
      notifyDeleted(showNotify, entityLabelText);
      if (apiPath.includes('/workflows')) {
        dispatchApprobationsWorkflowsRefresh();
      }
      setRefreshTrigger((n) => n + 1);
    } catch (error) {
      notifyError(showNotify, error?.response?.data?.error || pc('delete_error'));
    }
  };

  const handleView = (record) => {
    setViewingRecord(record);
    setShowViewModal(true);
  };

  const closeView = () => {
    setShowViewModal(false);
    setViewingRecord(null);
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(pc('bulk_delete_confirm', { count: selectedRecords.length }))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`${apiPath}/${id}`)));
      setSelectedRecords([]);
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPluralText);
      if (apiPath.includes('/workflows')) {
        dispatchApprobationsWorkflowsRefresh();
      }
      setRefreshTrigger((n) => n + 1);
    } catch (error) {
      notifyError(showNotify, error?.response?.data?.error || pc('delete_error'));
    }
  };

  const recordsArray = Array.isArray(records) ? records : [];
  const { dataColumns, statusColumn } = useMemo(
    () => partitionStatusFromColumns(columns),
    [columns],
  );
  const statCards = useMemo(() => sliceQawaemStats(computeStats(recordsArray), 4), [recordsArray, computeStats]);

  const defaultStatusOptions = useMemo(() => ([
    { value: 'actif', label: pl('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactif', label: pl('status_inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'brouillon', label: pl('status_draft'), icon: Tag, style: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
    { value: 'en_attente', label: pl('req_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'en_cours', label: pl('req_in_progress'), icon: Clock, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'approuve', label: pl('req_approved'), icon: CheckCircle, style: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    { value: 'rejete', label: pl('req_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'annule', label: pl('req_cancelled'), icon: XCircle, style: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  ]), [pl]);

  const statusOptions = statusOptionsProp || (statusColumn ? defaultStatusOptions : []);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const normalized = normalizeStatusValue(newStatus);
      let extraPayload = {};
      if (approbationsConfig.require_comment_on_reject && normalized === 'rejete') {
        const comment = window.prompt(
          pc('validation_reject_comment', { defaultValue: 'تعليق إلزامي عند الرفض' }),
        );
        if (!String(comment || '').trim()) {
          notifyError(showNotify, pc('validation_reject_comment', { defaultValue: 'تعليق إلزامي عند الرفض' }));
          return;
        }
        extraPayload = { notes: String(comment).trim() };
      }
      await localApiAdapter.put(`${apiPath}/${recordId}`, { [statusField]: newStatus, ...extraPayload });
      notifyUpdated(showNotify, entityLabelText);
      setRefreshTrigger((n) => n + 1);
    } catch (error) {
      notifyError(showNotify, error?.response?.data?.error || pc('save_error'));
    }
  };

  const statusLabelForValue = (val) => {
    if (!val) return '';
    const normalized = normalizeStatusValue(val);
    const map = [...WORKFLOW_STATUS, ...REQUEST_STATUS];
    const hit = map.find((s) => s.value === normalized);
    return hit ? pl(hit.labelKey) : (String(val).includes('.') ? pl('status_active') || 'نشط' : val);
  };

  const qawaemColumnDefs = useMemo(
    () => {
      const defs = dataColumns.map((col) => ({
        key: col.key,
        label: col.labelKey ? p(col.labelKey) : col.label,
        checked: columnVisibility[col.key] !== false,
      }));
      if (statusColumn) {
        defs.push({
          key: statusColumn.key,
          label: statusColumn.labelKey ? p(statusColumn.labelKey) : statusColumn.label,
          checked: columnVisibility[statusColumn.key] !== false,
        });
      }
      return defs;
    },
    [dataColumns, statusColumn, columnVisibility, p],
  );

  const handleToggleColumn = (key) => {
    if (key === 'actions') return;
    const col = [...dataColumns, ...(statusColumn ? [statusColumn] : [])].find((c) => c.key === key);
    if (col?.required) return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;

  const visibleDataColumns = useMemo(
    () => dataColumns.filter((c) => columnVisibility[c.key] !== false),
    [dataColumns, columnVisibility],
  );
  const showStatusColumn = statusColumn && columnVisibility[statusColumn.key] !== false;
  const tableColSpan = visibleDataColumns.length + (showStatusColumn ? 1 : 0) + 2;

  const getColumnFilterValue = useCallback((col, record) => {
    if (typeof col.filterValue === 'function') return String(col.filterValue(record, p, t, pl) ?? '');
    const raw = record[col.key];
    if (col.key === 'status') return normalizeStatusValue(raw);
    if (col.key === 'entity_type') return entityLabel(raw, pl) || String(raw ?? '');
    if ((col.key === 'created_at' || col.key === 'updated_at') && raw) {
      try {
        const dateLocale = qawaem || i18n.language?.startsWith('ar') ? 'ar-SA' : (i18n.language || 'fr-FR');
        return new Date(raw).toLocaleDateString(dateLocale);
      } catch {
        return String(raw ?? '');
      }
    }
    return String(raw ?? '');
  }, [p, t, pl, qawaem, i18n.language]);

  const isSelectColumn = useCallback(
    (col) => col.filterKind === 'select' || col.key === 'status',
    [],
  );

  const columnFilterOptions = useMemo(() => {
    const map = {};
    [...dataColumns, ...(statusColumn ? [statusColumn] : [])].forEach((col) => {
      if (col.filterOptions) {
        map[col.key] = col.filterOptions;
        return;
      }
      if (!isSelectColumn(col)) return;
      const values = [...new Set(recordsArray.map((r) => getColumnFilterValue(col, r)).filter(Boolean))];
      map[col.key] = values.sort().map((v) => ({
        value: v,
        label: col.key === 'status' ? statusLabelForValue(v) : v,
      }));
    });
    return map;
  }, [dataColumns, statusColumn, recordsArray, getColumnFilterValue, isSelectColumn, statusLabelForValue]);

  const columnFilterRules = useMemo(
    () => [...dataColumns, ...(statusColumn ? [statusColumn] : [])].map((col) => (
      isSelectColumn(col)
        ? exactColumnRule(col.key, (row) => getColumnFilterValue(col, row))
        : textColumnRule(col.key, (row) => getColumnFilterValue(col, row))
    )),
    [dataColumns, statusColumn, getColumnFilterValue, isSelectColumn],
  );

  const searchFiltered = useMemo(
    () => recordsArray.filter((record) => {
      if (filterRecords && !filterRecords(record)) return false;
      if (!shouldShowCardsToolbarSearch(viewMode) || !searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return [...dataColumns, ...(statusColumn ? [statusColumn] : [])].some((col) => {
        const val = getColumnFilterValue(col, record);
        return val && String(val).toLowerCase().includes(q);
      });
    }),
    [recordsArray, filterRecords, searchTerm, viewMode, dataColumns, statusColumn, getColumnFilterValue],
  );

  const filtered = useMemo(
    () => applyColumnFilters(searchFiltered, columnFilters, columnFilterRules),
    [searchFiltered, columnFilters, columnFilterRules],
  );

  const pagedFiltered = useMemo(
    () => sliceApprobationsListItems(filtered, approbationsConfig.itemsPerPage),
    [filtered, approbationsConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filtered);

  useEffect(() => {
    if (selectedRecords.length > 0) setColumnEditingKey(null);
  }, [selectedRecords, setColumnEditingKey]);

  const onSelectAll = () => {
    setColumnEditingKey(null);
    handleSelectAll();
  };

  const onSelectRecord = (id) => {
    setColumnEditingKey(null);
    handleSelectRecord(id);
  };

  const getFieldGridClass = (field) => {
    if (field.fullWidth || field.colSpan === 2 || field.colSpan === 3) return 'md:col-span-3';
    return 'min-w-0';
  };

  const fieldLabel = (field) => {
    const base = p(field.labelKey);
    return isFieldRequired(field) ? `${base} *` : base;
  };

  const formatViewFieldValue = useCallback((field, record) => {
    if (field.type === 'employee') {
      const name = record.requester_name
        || resolveApprobationsEmployeeDisplayName(record[field.key], employeeOptions, '');
      return name || '—';
    }
    if (field.type === 'chain_steps') {
      const jsonKey = field.jsonKey || field.key;
      const steps = enrichChainStepsWithEmployees(parseChainSteps(record[jsonKey]), employeeOptions);
      if (!steps.length) return '—';
      return steps.map((s) => {
        const who = s.employee_name || resolveApprobationsEmployeeDisplayName(s.employee_id, employeeOptions, '—');
        return s.role ? `${s.step}. ${who} (${s.role})` : `${s.step}. ${who}`;
      }).join('\n');
    }
    const raw = record[field.key];
    if (raw == null || raw === '') return '—';
    return String(raw);
  }, [employeeOptions]);

  const renderCodeField = () => {
    if (!codeField) return null;
    const value = formData[codeField] ?? editingRecord?.[codeField] ?? '';
    const readOnly = !codeEditable;
    const inputCls = cfgTheme.input;
    const readOnlyCls = isLight
      ? 'w-full px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-600 font-mono'
      : 'w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white/60 font-mono';
    return (
      <div key={codeField} className="min-w-0">
        <ErpFieldLabel
          label={p('field_code', { defaultValue: pc('field_code', { defaultValue: 'الرمز' }) })}
          className={erpFormLabelClass(isLight)}
        />
        <div data-testid={`approbations-field-${codeField}`}>
          <input
            data-testid={`approbations-input-${codeField}`}
            type="text"
            value={value}
            readOnly={readOnly}
            onChange={(e) => setFormData((prev) => ({ ...prev, [codeField]: e.target.value }))}
            className={readOnly ? readOnlyCls : inputCls}
            placeholder={p('placeholder_code', { defaultValue: pc('placeholder_code', { defaultValue: 'مثال: WF-001' }) })}
          />
        </div>
      </div>
    );
  };

  const renderField = (field) => {
    if (codeField && field.key === codeField) return null;
    const value = formData[field.key] ?? '';
    const onChange = (v) => {
      setFormData((prev) => (
        onFormFieldChange
          ? (onFormFieldChange(field.key, v, prev, employeeOptions) ?? { ...prev, [field.key]: v })
          : { ...prev, [field.key]: v }
      ));
    };
    const inputCls = cfgTheme.input;
    const readOnlyCls = isLight
      ? 'w-full px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-600'
      : 'w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white/60';
    const textareaCls = isLight
      ? 'w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
      : 'w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500';

    if (field.type === 'employee') {
      return (
        <div data-testid={`approbations-field-${field.key}`}>
          <CustomSelect
            dataTestId={`approbations-field-${field.key}`}
            value={value != null ? String(value) : ''}
            onChange={onChange}
            options={employeeSelectOptions}
            creatable={false}
            searchable
            placeholder={field.placeholder ? p(field.placeholder) : pc('select')}
          />
        </div>
      );
    }
    if (field.type === 'chain_steps') {
      const jsonKey = field.jsonKey || field.key;
      return (
        <ApprobationsChainStepsField
          value={formData[jsonKey]}
          onChange={(v) => setFormData((prev) => ({ ...prev, [jsonKey]: v }))}
          employeeOptions={employeeOptions}
          addStepLabel={p('chain_add_step', { defaultValue: 'إضافة خطوة' })}
          rolePlaceholder={p('chain_role_placeholder', { defaultValue: 'الدور (اختياري)' })}
          employeePlaceholder={p('chain_employee_placeholder', { defaultValue: 'المعتمد' })}
        />
      );
    }
    if (field.type === 'select') {
      const resolvedOpts = resolveFieldOptions?.(field, formData) ?? field.options ?? [];
      const opts = resolvedOpts.map((o) => ({
        value: o.value,
        label: o.labelKey ? pl(o.labelKey) : (o.label || o.value),
      }));
      return (
        <div data-testid={`approbations-field-${field.key}`}>
          <CustomSelect
            dataTestId={`approbations-field-${field.key}`}
            value={value}
            onChange={onChange}
            options={opts}
            creatable={false}
            searchable
            placeholder={field.placeholder ? p(field.placeholder) : pc('select')}
          />
        </div>
      );
    }
    if (field.type === 'date') {
      return (
        <div data-testid={`approbations-field-${field.key}`}>
          <CustomDatePicker
            data-testid={`approbations-input-${field.key}`}
            value={value}
            onChange={onChange}
            placeholder={field.placeholder ? p(field.placeholder) : pc('select')}
          />
        </div>
      );
    }
    if (field.type === 'textarea') {
      return (
        <div data-testid={`approbations-field-${field.key}`}>
          <textarea
            data-testid={`approbations-input-${field.key}`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={field.rows || 3}
            className={textareaCls}
            placeholder={field.placeholder ? p(field.placeholder) : ''}
          />
        </div>
      );
    }
    if (field.readOnly) {
      return (
        <div data-testid={`approbations-field-${field.key}`}>
          <input
            data-testid={`approbations-input-${field.key}`}
            type="text"
            value={value}
            readOnly
            className={readOnlyCls}
          />
        </div>
      );
    }
    return (
      <div data-testid={`approbations-field-${field.key}`}>
        <input
          data-testid={`approbations-input-${field.key}`}
          type={field.type || 'text'}
          value={value}
          onChange={(e) => onChange(field.type === 'number' ? e.target.value : e.target.value)}
          className={inputCls}
          placeholder={field.placeholder ? p(field.placeholder) : ''}
        />
      </div>
    );
  };

  const renderCell = (col, record) => {
    if (col.render) return col.render(record, p, t, pl);
    const raw = record[col.key];
    if ((col.key === 'created_at' || col.key === 'updated_at') && raw) {
      try {
        const dateLocale = qawaem || i18n.language?.startsWith('ar') ? 'ar-SA' : (i18n.language || 'fr-FR');
        return new Date(raw).toLocaleDateString(dateLocale);
      } catch {
        return raw;
      }
    }
    return raw ?? '—';
  };

  const handleExportCsv = () => {
    exportApprobationsCsv(filtered, columns, `${ns}-export.csv`);
    setExportOpen(false);
  };

  return (
    <>
    <QawaemErpListPageShell
      className=""
      title={p('title')}
      subtitle={p('subtitle')}
      icon={PageIcon}
      stats={statCards.map((stat) => ({
        key: stat.titleKey || stat.title,
        label: stat.titleKey ? p(stat.titleKey) : stat.title,
        value: stat.value,
        icon: stat.icon,
        variant: stat.variant || 'copper',
      }))}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          title={pc('delete_selection')}
        >
          <Trash2 size={16} />
          <span>{t('common.delete')} ({selectedRecords.length})</span>
        </button>
      ) : null}
      searchTerm={searchTerm}
      onSearchChange={shouldShowCardsToolbarSearch(viewMode) ? setSearchTerm : undefined}
      searchPlaceholder={p('search_placeholder')}
      columns={qawaemColumnDefs}
      visibleColumnCount={qawaemVisibleColumnCount}
      totalColumnCount={qawaemColumnDefs.length}
      onCreate={allowCreate ? openCreateModal : null}
      createLabel={p('new_item')}
      headerActions={(
        <button
          type="button"
          onClick={() => setRefreshTrigger((n) => n + 1)}
          className="refresh-button"
          title={t('common.refresh')}
        >
          <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      )}
      importExport={{
        importOpen: false,
        exportOpen,
        onToggleImport: () => {},
        onToggleExport: () => setExportOpen((v) => !v),
        importRef: exportRef,
        exportRef,
        importLabel: pc('import'),
        exportLabel: pc('export'),
        generatingLabel: t('common.loading'),
        isGeneratingPDF: false,
        onImportCSV: () => {},
        onImportExcel: () => {},
        onExportPDF: () => {},
        onExportExcel: () => {},
        onExportCSV: handleExportCsv,
      }}
    >
      {viewMode === 'table' ? (
        <div className="glass-table-scroll overflow-x-auto">
          <table className="w-full employees-table data-table" data-testid="approbations-data-table">
            <thead className="glass-table-head">
              <tr>
                <QawaemTableSelectAllHeader
                  allRowsSelected={allRowsSelected}
                  someRowsSelected={someRowsSelected}
                  onChange={onSelectAll}
                />
                {visibleDataColumns.map((col) => (
                  <TableColumnHeaderFilter
                    key={col.key}
                    columnKey={col.key}
                    label={col.labelKey ? p(col.labelKey) : col.label}
                    kind={isSelectColumn(col) ? 'select' : 'text'}
                    value={columnFilters[col.key] ?? ''}
                    onChange={(v) => setColumnFilter(col.key, v)}
                    options={columnFilterOptions[col.key] || []}
                    editingKey={columnEditingKey}
                    onEditingKeyChange={setColumnEditingKey}
                    thClassName="text-white font-semibold"
                    className="px-6 py-4"
                  />
                ))}
                {showStatusColumn ? (
                  <TableColumnHeaderFilter
                    columnKey={statusColumn.key}
                    label={statusColumn.labelKey ? p(statusColumn.labelKey) : statusColumn.label}
                    kind="select"
                    value={columnFilters[statusColumn.key] ?? ''}
                    onChange={(v) => setColumnFilter(statusColumn.key, v)}
                    options={columnFilterOptions[statusColumn.key] || []}
                    editingKey={columnEditingKey}
                    onEditingKeyChange={setColumnEditingKey}
                    align="center"
                    thClassName="text-white font-semibold"
                    className="px-6 py-4"
                  />
                ) : null}
                <QawaemTableHeaderActionsCell
                  label={pc('actions')}
                  columns={qawaemColumnDefs}
                  onToggle={handleToggleColumn}
                  visibleCount={qawaemVisibleColumnCount}
                  totalCount={qawaemColumnDefs.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 employees-table-body">
              {loading ? (
                <tr>
                  <td colSpan={tableColSpan} className="qawaem-table-empty-cell p-12 text-center text-white/60">{t('common.loading')}</td>
                </tr>
              ) : filtered.length === 0 ? (
                <QawaemTableEmptyRow
                  colSpan={tableColSpan}
                  icon={PageIcon}
                  message={p('empty_title')}
                  hint={p('empty_hint')}
                />
              ) : pagedFiltered.map((record) => (
                <tr
                  key={record.id}
                  className={`hover:bg-white/5 ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                  data-row-id={record.id}
                >
                  <QawaemTableSelectRowCell
                    checked={selectedRecords.includes(record.id)}
                    onChange={() => onSelectRecord(record.id)}
                  />
                  {visibleDataColumns.map((col) => (
                    <td key={col.key} data-col-key={col.key} className="px-6 py-4 text-white/90 text-sm">{renderCell(col, record)}</td>
                  ))}
                  {showStatusColumn ? (
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <QawaemClickableStatusBadge
                          status={normalizeStatusValue(record[statusField])}
                          recordId={record.id}
                          options={statusOptions}
                          onChange={handleStatusChange}
                          title={pc('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                        />
                      </div>
                    </td>
                  ) : null}
                  <QawaemTableStickyActionsTd>
                    <QawaemErpRowActions
                      onView={() => handleView(record)}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </QawaemTableStickyActionsTd>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : loading ? (
        <div className="p-12 text-center text-white/60">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center">
          <PageIcon className="h-12 w-12 text-white/30 mx-auto mb-4" />
          <p className="text-white/60">{p('empty_title')}</p>
          <p className="text-white/40 text-sm mt-1">{p('empty_hint')}</p>
        </div>
      ) : (
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
          {pagedFiltered.map((record) => (
            <div key={record.id} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 h-full min-w-0">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="text-white font-semibold">{record.name || record.title || record[codeField]}</p>
                  <p className="text-white/50 text-xs mt-1">{record[codeField] || record.workflow_code || record.request_code || record.template_code}</p>
                </div>
                <QawaemErpRowActions
                  compact
                  size={14}
                  onView={() => handleView(record)}
                  onEdit={() => handleEdit(record)}
                  onDelete={() => handleDelete(record.id)}
                  viewTitle={t('common.view')}
                  editTitle={t('common.edit')}
                  deleteTitle={t('common.delete')}
                />
              </div>
              {statusColumn ? (
                <div className="mb-2">
                  <QawaemClickableStatusBadge
                    status={normalizeStatusValue(record[statusField])}
                    recordId={record.id}
                    options={statusOptions}
                    onChange={handleStatusChange}
                  />
                </div>
              ) : null}
              {dataColumns.slice(1, 4).map((col) => (
                <p key={col.key} className="text-white/60 text-sm">
                  <span className="text-white/40">{col.labelKey ? p(col.labelKey) : col.label}: </span>
                  {renderCell(col, record)}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
    </QawaemErpListPageShell>

    <div data-i18n-ready={i18nNsReady ? 'true' : 'false'} />

      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingRecord ? p('modal_edit') : p('modal_new')}
        icon={PageIcon}
        formId="approbations-form"
        saveLabel={t('common.save')}
        showSaveButton
        saving={loading}
        testId="approbations-modal"
        tabs={showFormTabs ? formTabs : undefined}
        activeTab={formTab}
        onTabChange={setFormTab}
        tabsAriaLabel={editingRecord ? p('modal_edit') : p('modal_new')}
      >
        <form id="approbations-form" data-testid="approbations-form" className={ERP_MODAL_FORM_CLASS} onSubmit={handleSubmit}>
          {resolvedFormSections.map((section, sectionIndex) => (!showFormTabs || formTab === section.id) && (
            <div key={section.id} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {sectionIndex === 0 && renderCodeField()}
              {section.fields.map((field) => (
                <div key={field.key} className={field.type === 'chain_steps' ? 'md:col-span-3 min-w-0' : getFieldGridClass(field)}>
                  {field.type !== 'chain_steps' ? (
                    <ErpFieldLabel label={fieldLabel(field)} className={erpFormLabelClass(isLight)} />
                  ) : (
                    <ErpFieldLabel label={fieldLabel(field)} className={`${erpFormLabelClass(isLight)} mb-2`} />
                  )}
                  {renderField(field)}
                </div>
              ))}
            </div>
          ))}
        </form>
      </QawaemErpCrudModal>

      {showViewModal && viewingRecord ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={closeView}
          title={p('view_title')}
          icon={FileSearch}
          testId="approbations-view-modal"
        >
          <div className="space-y-3">
            {columns.map((col) => (
              <div
                key={col.key}
                className={`flex justify-between gap-4 border-b pb-2 ${isLight ? 'border-slate-100' : 'border-white/5'}`}
              >
                <span className={`text-sm shrink-0 ${isLight ? 'text-slate-500' : 'text-white/50'}`}>
                  {col.labelKey ? p(col.labelKey) : col.label}
                </span>
                <span className={`text-sm text-end ${cfgTheme.title}`}>{renderCell(col, viewingRecord)}</span>
              </div>
            ))}
            {formSections.flatMap((section) => section.fields
              .filter((f) => !columns.some((c) => c.key === f.key))
              .map((field) => (
                <div
                  key={field.key}
                  className={`flex justify-between gap-4 border-b pb-2 ${isLight ? 'border-slate-100' : 'border-white/5'}`}
                >
                  <span className={`text-sm shrink-0 ${isLight ? 'text-slate-500' : 'text-white/50'}`}>
                    {p(field.labelKey)}
                  </span>
                  <span className={`text-sm text-end whitespace-pre-wrap ${cfgTheme.title}`}>
                    {formatViewFieldValue(field, viewingRecord)}
                  </span>
                </div>
              )))}
          </div>
        </QawaemErpViewModal>
      ) : null}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
}
